/**
 * S2 · CASE — the deep-link target, the heart of the design.
 *
 * Six bands, always in this order, ALWAYS PRESENT. A band with no data says
 * why it has none; bands never disappear, because their absence is
 * information.
 *
 *   1 ARRIVAL    sightings (lossy ⇒ `≥n`, never a ratio) + the detect Span
 *   2 RACE       two lanes, one start line; LOST AT; the boundary chip;
 *                one generated adjudication sentence
 *   3 DECISION   a refusal IS a decision and renders as one
 *   4 ORDER      the post-to-fill waterfall + three labelled totals, each
 *                with its seam count
 *   5 OUTCOME    the verdict pair — MACHINE beside STRATEGY
 *   6 AMENDMENTS a case is never closed, only enriched
 *
 * plus the RAW drawer: the literal journal lines with file and byte offset.
 * If the card is wrong, the substrate proves it in one tap. A surface that
 * cannot be audited against its own inputs will not be trusted, and trust is
 * the entire point.
 */

import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { api, type Span } from "../lib/api";
import { CloseEvidence, Money, Pairing } from "../lib/money";
import { navigate } from "../lib/route";
import { ackLabel, DotStrip, SpanBar, Total } from "../lib/span";
import { stamp } from "../lib/time";
import { VirtualList } from "../lib/VirtualList";

const BANDS = ["arrival", "race", "decision", "order", "outcome", "amendments"] as const;

export function CaseView({ postId, band }: { postId: string; band?: string }) {
  const q = useQuery({ queryKey: ["case", postId], queryFn: () => api.case(postId) });
  const rootRef = useRef<HTMLDivElement>(null);

  // A push deep-links to /c/<post_id>#order, so the case must open ALREADY
  // SCROLLED to that band. No navigation, no home page (design §5.1).
  useEffect(() => {
    if (!band || !q.data) return;
    const el = rootRef.current?.querySelector(`#band-${band}`);
    if (el) el.scrollIntoView({ block: "start" });
  }, [band, q.data]);

  if (q.isLoading) return <div className="stub">loading case…</div>;
  if (q.error) {
    // Fail-closed and INDISTINGUISHABLE: another tenant's post_id and an id
    // that exists nowhere produce the same 404 and the same sentence. A
    // different message would confirm the row exists somewhere. It is a
    // REFUSAL, not a negative number, so it is never red.
    return <div className="stub stub--error">no such case in this tenant</div>;
  }

  const { case: c, bands } = q.data;
  const hopSpans: Array<Span | null> = [
    bands.arrival?.detect ?? null,
    bands.decision?.score ?? null,
    ...(bands.order?.orders?.[0]?.waterfall?.slice(2) ?? [null, null, null, null]),
  ];

  return (
    <div className="surface case" ref={rootRef}>
      <header className="surface__head">
        <h1 className="surface__title case__outcome">{c.outcome_word}</h1>
        <div className="surface__meta mono">
          {c.source} · {c.kind ?? "—"} · kind {c.kind_source ?? "—"} · {stamp(c.created_at)} {c.created_at_clock ?? ""}
        </div>
      </header>

      <div className="surface__body">
        <div className="band__title">
          CASE {c.post_id} · {c.settlement} · rev {c.revision}
        </div>
        <DotStrip spans={hopSpans} />

        {/* dir="auto": a Farsi body laid out LTR reads as a broken sentence. */}
        {c.content_text && (
          <p className="case__text" dir="auto">
            {c.content_text}
          </p>
        )}
        {c.content_text_en && (
          <p className="muted case__text" dir="auto">
            <span className="chip">EN</span> {c.content_text_en}
          </p>
        )}
        {!c.content_text && (
          <p className="dim">
            — no body on this case (media-only arrival, or the journal carried no text)
          </p>
        )}

        <nav className="case__anchors mono">
          {BANDS.map((b) => (
            <a key={b} href={`#band-${b}`}>
              {b}
            </a>
          ))}
        </nav>

        <Band id="arrival" n={1} title="ARRIVAL">
          <Arrival arrival={bands.arrival} />
        </Band>

        <Band id="race" n={2} title="RACE">
          <Race race={bands.race} />
        </Band>

        <Band id="decision" n={3} title="DECISION">
          <div className="mono">
            {bands.decision.signal ?? "NO TRADE"}
            {bands.decision.no_trade_reason ? ` — why: ${bands.decision.no_trade_reason}` : ""}
          </div>
          {/* A refusal IS a decision and renders as one. */}
          <SpanBar span={bands.decision.score} label="score" scale={0.08} />
        </Band>

        <Band id="order" n={4} title="ORDER">
          <OrderBand order={bands.order} />
        </Band>

        <Band id="outcome" n={5} title="OUTCOME">
          <Verdicts outcome={bands.outcome} orders={bands.order?.orders ?? []} />
        </Band>

        <Band id="amendments" n={6} title="AMENDMENTS">
          {(!bands.amendments || bands.amendments.length === 0) && <div className="dim">no late enrichment yet</div>}
          {(bands.amendments ?? []).map((a: any) => (
            <div key={a.revision} className="mono amend">
              rev {a.revision} · {stamp(a.at)} · {a.kind} ·{" "}
              <span className={a.material ? "" : "dim"}>{a.material ? "material" : "immaterial"}</span>
              {a.note ? ` · ${a.note}` : ""}
            </div>
          ))}
          <div className="span__note">
            material ⇒ a new AMEND feed entry · immaterial ⇒ in place. Never a silent mutation, never a re-sort.
          </div>
        </Band>

        <RawDrawer postId={postId} refs={q.data.raw_refs ?? []} />
      </div>
    </div>
  );
}

function Band({
  id,
  n,
  title,
  children,
}: {
  id: string;
  n: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="band" id={`band-${id}`}>
      <div className="band__title">
        {n} · {title}
      </div>
      {children}
    </section>
  );
}

/* ------------------------------------------------------------- 1 · ARRIVAL */

function Arrival({ arrival }: { arrival: any }) {
  const sightings: any[] = arrival?.sightings ?? [];
  const peers = new Set(sightings.filter((s) => s.via === "mesh").map((s) => s.peer_id)).size;
  return (
    <>
      {sightings.length === 0 && <div className="dim">no arrival rows — this case was reconstructed from the primary journal</div>}
      {sightings.map((s, i) => (
        <div key={i} className="mono sighting">
          {i === 0 ? "first" : "then "} {s.node_id ?? "—"} · {s.via ?? "—"}
          {s.peer_id ? ` ← ${s.peer_id}` : ""} · {stamp(s.arrival_at)}
          {s.transit_ms !== null && s.transit_ms !== undefined && (
            <span className={s.transit_ms < 0 ? "down" : ""} title="mesh transit; a NEGATIVE value is hard clock-skew proof">
              {" "}
              ↔ {s.transit_ms < 0 ? "−" : ""}
              {Math.abs(s.transit_ms)}ms{s.transit_ms < 0 ? " (clock skew — peer ahead of us)" : ""}
            </span>
          )}
          {s.dropped_reason && <span className="chip chip--down">{s.dropped_reason}</span>}
        </div>
      ))}
      {/* Sightings are lossy BY CONSTRUCTION: bounded queue, put_nowait, and
          the drop counter is journaled nowhere. So the count is a LOWER
          BOUND and no ratio is ever computed from it (§6.6 / D24). */}
      <div className="mono">
        ≥{sightings.length} arrival{sightings.length === 1 ? "" : "s"}
        {peers > 0 ? ` · ${peers} peer${peers === 1 ? "" : "s"}` : ""}
      </div>
      <SpanBar span={arrival?.detect} label="detect" scale={0.08} />
      <div className="span__note">⚠ {arrival?.caveat}</div>
    </>
  );
}

/* ---------------------------------------------------------------- 2 · RACE */

export function Race({ race }: { race: any }) {
  if (!race?.present) return <div className="dim">{race?.why ?? "no race recorded"}</div>;
  const slots: any[] = race.fast_slots ?? [];
  const answer = race.fast_answer ?? {};
  const gate = race.fast_gate ?? {};
  /* `smart_calls` is an OBJECT, not a list:
     `{ all_ms: {instrument: [ms, …]}, all_directions, all_intensities,
        results, note }`. It was typed `any[]` and mapped, so `.map is not a
     function` threw out of render and — with no boundary above it — took the
     WHOLE console blank the instant anyone opened a case. Both halves of that
     are fixed: the shape here, and the boundary in App.tsx.
     Per-instrument arrays are COMPLETION ORDER, never slot identity; the
     sibling maps are keyed by the same instrument when the journal carried
     them and are simply absent when it did not. */
  const smartRaw = race.smart_calls;
  const byInstrument = (m: unknown, k: string): unknown =>
    m && typeof m === "object" && !Array.isArray(m) ? (m as Record<string, unknown>)[k] : undefined;
  const smart: Array<{ instrument: string; ms: number[]; direction: unknown; intensity: unknown; result: unknown }> =
    smartRaw && typeof smartRaw === "object" && !Array.isArray(smartRaw)
      ? Object.entries((smartRaw.all_ms ?? {}) as Record<string, unknown>).map(([instrument, ms]) => ({
          instrument,
          ms: Array.isArray(ms) ? (ms as number[]) : [],
          direction: byInstrument(smartRaw.all_directions, instrument),
          intensity: byInstrument(smartRaw.all_intensities, instrument),
          result: byInstrument(smartRaw.results, instrument),
        }))
      : [];
  return (
    <>
      <div className="mono muted">start line {stamp(race.start_line)}</div>

      <div className="lane">
        <div className="lane__name">FAST</div>
        <div className="lane__body">
          <div className="race__slots">
            {slots.map((s, i) => (
              <span
                key={i}
                className={`race__bar${s.err ? " race__bar--err" : ""}${race.fast_winner_idx === s.idx ? " race__bar--win" : ""}`}
                style={{ width: Math.max(3, Math.min(80, (s.ms ?? 0) / 8)) }}
                title={s.err ? `slot ${s.idx} errored: ${s.err}` : `slot ${s.idx}: ${s.ms}ms`}
              />
            ))}
            <span className="mono">
              {race.fast_p2d_ms ?? "∅"}ms
              {race.fast_winner_idx !== null && race.fast_winner_idx !== undefined
                ? ` · winner slot ${race.fast_winner_idx}`
                : ""}
            </span>
          </div>
          {race.fast_hedge_saved_ms !== null && race.fast_hedge_saved_ms !== undefined && (
            <div className="mono muted" title="'the hedge helped' and 'the fast path was earlier' are different claims">
              hedge saved ⌂{race.fast_hedge_saved_ms}ms
            </div>
          )}
          <div className="mono">
            d={answer.d ?? "—"} c={answer.c ?? "—"} q={answer.q ?? "—"} cond={String(answer.cond ?? "—")}
          </div>
          {race.fast_lost_at ? (
            /* Categorical, not a number: the gate that refused is drawn with
               the ✗ mark and rank, never with --data-down. */
            <div className="mono">✗ LOST AT: {race.fast_lost_at}</div>
          ) : (
            <div className="mono">gate passed</div>
          )}
          {(race.fast_boundary === 1 || race.fast_boundary === true) && (
            <div className="chip">
              BOUNDARY — would_fire@80 ≠ would_fire@85; this post's fate depends on the gate constant
            </div>
          )}
          <div className="mono muted">
            mode {race.fast_mode ?? "—"} · fired {race.fast_fired ? "yes" : "no"} · claimed{" "}
            {race.fast_claimed ? "yes" : "no"}
            {gate.gate_c !== undefined ? ` · gate_c ${gate.gate_c}` : ""}
          </div>
          {race.fast_alert && (
            <div className="callout callout--alarm">⚠ WRONG-DIRECTION FIRE — {race.fast_alert}</div>
          )}
        </div>
      </div>

      <div className="lane">
        <div className="lane__name">SMART</div>
        <div className="lane__body">
          <div className="mono">
            {race.smart_p2d_ms ?? "∅"}ms
            {race.smart_analysis_ms ? ` · analysis ${race.smart_analysis_ms}ms` : ""}
          </div>
          {smart.length === 0 && <div className="dim">no per-call breakdown in this journal</div>}
          {smart.map((k) => (
            <div key={k.instrument} className="mono muted">
              {k.instrument}{" "}
              {k.ms.length ? k.ms.map((ms) => `${Math.round(ms)}ms`).join(" · ") : "∅"}
              {k.direction === null || k.direction === undefined ? "" : ` · ${String(k.direction)}`}
              {k.intensity === null || k.intensity === undefined ? "" : ` · i${String(k.intensity)}`}
              {k.result === null || k.result === undefined ? "" : ` → ${String(k.result)}`}
            </div>
          ))}
          {/* The server ships its own caveat for this lane; print THAT, and
              fall back to the hand-written one only when it does not. */}
          <div className="span__note">
            {(smartRaw && typeof smartRaw === "object" && !Array.isArray(smartRaw) && smartRaw.note) ||
              "completion order — the smart lane does not journal a slot index (the fast lane does; P14)"}
          </div>
          <div className="mono muted">route {race.smart_route ?? "none"}</div>
        </div>
      </div>

      <div className="mono">
        head start {race.head_start_ms === null || race.head_start_ms === undefined ? "∅" : `≈${race.head_start_ms}ms`}{" "}
        · WINNER:{" "}
        <span className={race.winner === "UNVERIFIED" ? "race__winner--unverified" : ""}>{race.winner ?? "—"}</span>
      </div>
      {/* One generated sentence: the operator should not have to assemble the
          verdict from six fields. */}
      <p className="adjudication">{race.adjudication}</p>
      {race.winner === "UNVERIFIED" && (
        <div className="callout callout--alarm">
          A fire whose Gemini join timed out holds a live position and writes <code>alert: null</code>, which would
          otherwise read as clean. It is a fault class, not a clean fire.
        </div>
      )}
    </>
  );
}

/* --------------------------------------------------------------- 4 · ORDER */

const WATERFALL_LABELS = ["detect", "score", "dispatch", "submit", "confirm", "fill"];

function OrderBand({ order }: { order: any }) {
  const orders: any[] = order?.orders ?? [];
  if (orders.length === 0) return <div className="dim">{order?.why ?? "none — the decision refused"}</div>;
  return (
    <>
      {orders.map((o) => (
        <div key={o.trade_id} className="order">
          <div className="order__head mono">
            <button className="linkish" onClick={() => navigate({ name: "order", tradeId: o.trade_id })}>
              {o.trade_id}
            </button>
            <span>{o.venue}</span>
            <Pairing pairing={o.pairing} />
            <span className="span__note">ack measured as {o.ack_label ?? ackLabel(o.order_post_semantics)}</span>
          </div>

          <div className="waterfall">
            <div className="mono muted">post · 0 ms</div>
            {(o.waterfall ?? []).map((s: Span | null, i: number) => (
              <SpanBar key={i} span={s} label={WATERFALL_LABELS[i]} scale={0.08} />
            ))}
          </div>

          <hr />
          {(o.totals ?? []).map((t: any) => (
            <Total key={t.label} label={t.label} ms={t.ms} seams={t.seams} note={t.note} />
          ))}

          <div className="mono muted order__facts">
            {/* No field, no number: there is no absolute submit or ack
                timestamp anywhere in the journals, so this band renders
                DURATIONS and never a wall stamp (§6.3). `deal_ref` appears
                only on rejected / unconfirmed rows, never on a happy open. */}
            requested {o.size ?? "—"} @ market · filled {o.fill_level ?? "—"} @ {o.entry_price ?? "—"}
            {o.entry_est !== null && o.entry_est !== undefined ? ` · entry est ${o.entry_est}` : ""}
            {o.slippage_bps !== null && o.slippage_bps !== undefined ? ` → slippage ${o.slippage_bps} bp` : ""}
          </div>
          <div className="mono muted">
            deal_id {o.deal_id ?? "—"}
            {o.deal_ref ? ` · deal_ref ${o.deal_ref} (present only on a rejected/unconfirmed row)` : ""}
            {" · state "}
            {o.state}
          </div>
        </div>
      ))}
    </>
  );
}

/* ------------------------------------------------------------- 5 · OUTCOME */

function Verdicts({ outcome, orders }: { outcome: any; orders: any[] }) {
  const m = outcome?.machine ?? {};
  const strategy = outcome?.strategy;
  const primary = orders[0];
  const unverified = primary && (primary.close_evidence === "NONE" || primary.close_evidence === "PRICE_INFERRED");
  return (
    <>
      <div className="verdict-pair">
        <div>
          <span className="verdict">MACHINE</span>{" "}
          {m.verdict === "NO_FAULT" ? (
            // The word "CLEAN" does not exist in this product. The positive
            // verdict is a statement about WHAT WAS CHECKED (§6.9).
            <>
              <span>NO FAULT</span> · {m.checks ?? 0} checks ·{" "}
              <span className="chip" title="the invariants that could not be evaluated on this case">
                {m.not_evaluable ?? 0} not evaluable
              </span>
            </>
          ) : (
            /* A fault CLASS is a category, not a negative number. */
            <span className="verdict">{m.verdict ?? "UNKNOWN"}</span>
          )}
        </div>
        <div>
          <span className="verdict">STRATEGY</span>{" "}
          {strategy && strategy !== "NONE" ? (
            /* WIN / LOSS is categorical; the colour belongs to the signed
               money figure below, and to nothing else. */
            <span className="verdict">{strategy}</span>
          ) : (
            <span className="dim">— (no attested P&amp;L; an estimate never produces a verdict)</span>
          )}
        </div>
      </div>

      {primary && (
        <div className="outcome__money mono">
          <Money a={primary.pnl} big />
          <CloseEvidence evidence={primary.close_evidence} />
          <span className="span__note">exit {primary.close_reason ?? "—"}</span>
        </div>
      )}

      {unverified && (
        /* The design doing its actual job: preventing a machinery failure
           from being scored as strategy performance. */
        <div className="callout callout--alarm">
          <strong>What this means:</strong> the strategy's stop may never have been hit. The position's close could not
          be proved from a deal-tied record. Do not attribute this to the cell's edge.
          <div className="row-actions">
            <button onClick={() => navigate({ name: "gates" })}>gates</button>
            <button onClick={() => navigate({ name: "faults" })}>faults</button>
          </div>
        </div>
      )}
      <div className="span__note">{m.note}</div>
    </>
  );
}

/* ------------------------------------------------------------- RAW drawer */

function RawDrawer({ postId, refs }: { postId: string; refs: any[] }) {
  const [open, setOpen] = useState(false);
  const q = useQuery({ queryKey: ["case-raw", postId], queryFn: () => api.caseRaw(postId), enabled: open });
  const rows = (open ? (q.data?.refs ?? refs) : refs) as any[];
  return (
    <details
      className="raw"
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary className="muted">raw journal lines — file · byte offset · hash</summary>
      <VirtualList
        rows={rows}
        ariaLabel="raw journal references"
        className="vlist vlist--raw"
        estimateSize={26}
        rowKey={(r, i) => `${r.journal}:${r.byte_offset}:${i}`}
        renderRow={(r) => (
          <div className="mono raw__row">
            <span className="chip">{r.band}</span> {r.journal} @{r.byte_offset}+{r.length}{" "}
            <span className="dim">{(r.line_hash ?? "").slice(0, 12)}</span>
          </div>
        )}
        emptyLabel="no byte offsets recorded for this case"
      />
    </details>
  );
}
